import React from 'react';
import { useUID } from 'react-uid';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/system';
import Box, { BoxProps } from '@mui/system/Box';
import { styled, useThemeProps } from '@mui/material/styles';
import { shouldForwardProp } from '../../util/shouldForwardProps';

const useUtilityClasses = (props: InputColorProps) => {
  const { error, classes = {} } = props;
  const slots = {
    root: ['root', error && 'error'],
    label: ['label', error && 'error'],
    input: ['input', error && 'error'],
    helperText: ['helperText', error && 'error'],
    colorPreview: ['colorPreview'],
  };

  const composedClasses: Record<string, string> = {};
  Object.keys(slots).forEach((key) => {
    const slotClasses = slots[key as keyof typeof slots];
    composedClasses[key] = slotClasses
      .filter(Boolean)
      .map((cls) => (typeof cls === 'string' ? `FSInputColor-${cls}` : ''))
      .join(' ');
  });

  return {
    ...classes,
    ...composedClasses,
  };
};

interface OwnInputColorProps {
  label?: string | null;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  slots?: { label?: React.ElementType };
  slotProps?: { label?: any };
  classes?: Record<string, string>;
  sx?: BoxProps['sx'];
  sxInput?: BoxProps['sx'];
  ownerState?: any;
  required?: boolean;
  id?: string;
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  showPreview?: boolean;
  [key: string]: any;
}

export type InputColorProps = OwnInputColorProps;

const InputColor = React.forwardRef(function CustomInputColor(
  inProps: InputColorProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const props = useThemeProps({ props: inProps, name: 'FSInputColor' });
  const uniqueId = useUID();
  const id = props.id || `FSInputColor-${uniqueId}`;
  const name = props.name || id;
  const classes = useUtilityClasses(props);
  const colorInputRef = React.useRef<HTMLInputElement>(null);

  const [colorPreviewBg, setColorPreviewBg] = React.useState(
    props.value || '#000000'
  );

  React.useEffect(() => {
    setColorPreviewBg(props.value || '#000000');
  }, [props.value]);

  const {
    label,
    error,
    helperText,
    fullWidth,
    slots,
    slotProps,
    sx,
    sxInput,
    ownerState,
    required,
    value = '#000000',
    onChange,
    disabled,
    placeholder,
    ...otherProps
  } = props;

  const handleButtonClick = () => {
    if (!disabled && colorInputRef.current) {
      colorInputRef.current.click();
    }
  };

  return (
    <StyledContainerRoot
      className={classes.root}
      sx={sx as any}
      fullWidth={fullWidth}
    >
      <StyledColorInput
        id={id}
        name={name}
        ref={colorInputRef}
        type="color"
        className={classes.input}
        value={colorPreviewBg}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setColorPreviewBg(event.target.value);
          onChange && onChange(event.target.value);
        }}
        disabled={disabled}
        ownerState={{ ...ownerState, value }}
        sx={sxInput}
        {...otherProps}
      />
      <StyledColorButton
        onClick={handleButtonClick}
        disabled={disabled}
        fullWidth={fullWidth}
        className={classes.input}
        colorValue={colorPreviewBg}
      >
        <StyledColorPreview style={{ backgroundColor: colorPreviewBg }} />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {label || 'Select Color'}
        </Typography>
      </StyledColorButton>
      {helperText && (
        <Typography
          color={error ? 'error' : undefined}
          sx={{ lineHeight: '2rem' }}
          className={classes.helperText}
        >
          {helperText}
        </Typography>
      )}
    </StyledContainerRoot>
  );
});

export default InputColor;

const StyledContainerRoot = styled(Box, {
  shouldForwardProp,
})<InputColorProps>(({ theme, ...props }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: '1rem 0',
  gap: theme.spacing(1),
  ...(props.fullWidth && {
    width: '100%',
  }),
}));

const StyledColorInput = styled('input', {
  shouldForwardProp,
})<{ ownerState?: any }>(({ theme, ...props }) => ({
  position: 'absolute',
  opacity: 0,
  width: 0,
  height: 0,
  pointerEvents: 'none',
}));

const StyledColorButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'colorValue',
})<{ fullWidth?: boolean; colorValue?: string; disabled?: boolean }>(
  ({ theme, fullWidth, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: '12px 16px',
    border: '3px solid transparent',
    borderRadius: '12px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    borderWidth: theme.palette.mode === 'dark' ? '3px' : '2px',
    borderStyle: 'solid',
    borderColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[200] : '#C6C6C6',
    background: 'transparent',
    transition: 'all 0.2s ease',
    minHeight: '48px',
    userSelect: 'none',

    '&:hover': !disabled && {
      borderColor: alpha(
        theme.palette.primary.main,
        1 - theme.palette.action.hoverOpacity
      ),
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },

    '&:active': !disabled && {
      transform: 'scale(0.98)',
    },

    ...(fullWidth && {
      width: '100%',
    }),

    ...(disabled && {
      borderColor: '#9E9E9E!important',
      opacity: 0.6,
      backgroundColor: '#f5f5f5',
    }),
  })
);

const StyledColorPreview = styled(Box)(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  border: `2px solid ${
    theme.palette.mode === 'dark' ? theme.palette.grey[200] : '#C6C6C6'
  }`,
  flexShrink: 0,
  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
}));
